import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { Permissions } from "utils/permissions/permissionConstants";
import {
  getProjectFeatureStatus,
  handleToast,
  mapProjectFeatureStatusesToFeatureStatus,
} from "utils";
import { formatAiMergeReportToast } from "utils/aiRunMergePolicy";
import { registerForegroundAiWatcher } from "utils/aiRunTransitions";
import type { AiMergeReportPayload } from "utils/aiRunMergePolicy";
import { status as statusEnum } from "store/types/enum";
import { characterKeys, coverageKeys, projectKeys } from "queries";
import { sessionKeys } from "queries/profile.keys";
import { useScriptAiRuns } from "hooks/project/useScriptAiRuns";
import { useScriptAiRunStatusPolling } from "hooks/project/useScriptAiRunStatusPolling";
import { ScriptAiHubDialog } from "./ScriptAiHubDialog";
import {
  ProjectScriptAiHubContext,
  isRunAiHubPipeline,
  type OpenRunAiHub,
  type ProjectScriptAiHubContextValue,
  type RunAiHubPipeline,
} from "./projectScriptAiHubContext";
import {
  ScriptAiHubEditorBridgeContext,
  type ScriptAiHubEditorBridgeValue,
} from "./ScriptAiHubEditorBridge";

const HUB_COMPLETION_FEATURES = ["script_breakdown", "character_breakdown", "coverage"] as const;

export { useOpenProjectScriptAiHub, useProjectScriptAiHub } from "./projectScriptAiHubContext";

export function ProjectScriptAiHubProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const pid = String(projectId || "");
  const did = String(documentId || "");
  const { canProject } = useProjectAuth(hasValidIds ? pid : "");
  const canTriggerScriptAi = canProject(Permissions.PROJECT_UPDATE);

  const [hubOpen, setHubOpen] = useState(false);
  const [preselectedPipeline, setPreselectedPipeline] = useState<RunAiHubPipeline | null>(null);
  const [editorBridge, setEditorBridge] = useState<ScriptAiHubEditorBridgeValue | null>(null);

  const scriptAiRuns = useScriptAiRuns(hasValidIds ? pid : "", hasValidIds ? did : "");

  const { project: projectForAi, clearScriptAiPipelineLock } = useScriptAiRunStatusPolling({
    projectId: pid,
    documentId: did,
    hasValidIds,
    isAnyMutationPending: scriptAiRuns.isAnyPending,
  });

  const prevHubFeatureStatusRef = useRef<Record<string, string | undefined>>({});

  // Tell the global AI-run watcher this project's hub features are handled
  // here while mounted, so background completions aren't double-toasted.
  useEffect(() => {
    if (!hasValidIds || !pid) return;
    return registerForegroundAiWatcher(pid, HUB_COMPLETION_FEATURES);
  }, [hasValidIds, pid]);

  useEffect(() => {
    if (!projectForAi?.featureStatuses?.length || !hasValidIds) return;

    const pidStr = String(projectForAi.projectId ?? pid);
    const mapped = mapProjectFeatureStatusesToFeatureStatus(projectForAi.featureStatuses, pidStr);

    for (const feature of HUB_COMPLETION_FEATURES) {
      const st = getProjectFeatureStatus(mapped, feature)?.status;
      const prev = prevHubFeatureStatusRef.current[feature];

      if (prev === FEATURE_STATUS_RERUNNING || prev === FEATURE_STATUS_PENDING) {
        if (st === FEATURE_STATUS_COMPLETED) {
          const row = projectForAi.featureStatuses?.find((fs) => fs.feature === feature);
          const meta = row?.metadata as { merge_report?: AiMergeReportPayload } | undefined;
          const doneMsg = meta?.merge_report ? formatAiMergeReportToast(meta.merge_report) : null;

          if (feature === "character_breakdown") {
            handleToast(doneMsg ?? "Character breakdown finished.", statusEnum.SUCCESS);
            void queryClient.invalidateQueries({ queryKey: characterKeys.all });
            void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
          } else if (feature === "coverage") {
            handleToast(doneMsg ?? "Coverage finished.", statusEnum.SUCCESS);
            void queryClient.invalidateQueries({ queryKey: coverageKeys.detail(pid, did) });
          } else {
            handleToast(doneMsg ?? "Script breakdown finished.", statusEnum.SUCCESS);
            scriptAiRuns.invalidateScriptBreakdownQueries();
            void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
          }

          void queryClient.invalidateQueries({ queryKey: projectKeys.detail(pid) });
          clearScriptAiPipelineLock();
        } else if (st === FEATURE_STATUS_FAILED) {
          const label =
            feature === "character_breakdown"
              ? "Character breakdown"
              : feature === "coverage"
                ? "Coverage"
                : "Script breakdown";
          handleToast(`${label} failed. Please try again.`, statusEnum.ERROR);
          if (feature === "character_breakdown") {
            void queryClient.invalidateQueries({ queryKey: characterKeys.all });
          } else if (feature === "coverage") {
            void queryClient.invalidateQueries({ queryKey: coverageKeys.detail(pid, did) });
          } else {
            scriptAiRuns.invalidateScriptBreakdownQueries();
          }
          void queryClient.invalidateQueries({ queryKey: projectKeys.detail(pid) });
          void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
          clearScriptAiPipelineLock();
        }
      }

      prevHubFeatureStatusRef.current[feature] = st;
    }
  }, [clearScriptAiPipelineLock, did, hasValidIds, pid, projectForAi, queryClient, scriptAiRuns]);

  const openRunAiHub = useCallback<OpenRunAiHub>((pipeline) => {
    // Guard against a click event passed by `onClick={openRunAiHub}`: only a
    // known pipeline preselects a row.
    setPreselectedPipeline(isRunAiHubPipeline(pipeline) ? pipeline : null);
    setHubOpen(true);
  }, []);

  const handleHubOpenChange = useCallback((nextOpen: boolean) => {
    setHubOpen(nextOpen);
    if (!nextOpen) setPreselectedPipeline(null);
  }, []);

  const hubContextValue = useMemo<ProjectScriptAiHubContextValue | null>(() => {
    if (!hasValidIds || !canTriggerScriptAi || !projectForAi) return null;
    return { openRunAiHub, isAvailable: true };
  }, [canTriggerScriptAi, hasValidIds, openRunAiHub, projectForAi]);

  const scriptHasMinimalRunContent = editorBridge?.scriptHasMinimalRunContent ?? true;

  return (
    <ScriptAiHubEditorBridgeContext.Provider value={setEditorBridge}>
      <ProjectScriptAiHubContext.Provider value={hubContextValue}>
        {children}
        {hasValidIds && projectForAi ? (
          <ScriptAiHubDialog
            open={hubOpen}
            onOpenChange={handleHubOpenChange}
            preselectedPipeline={preselectedPipeline}
            projectId={pid}
            documentId={did}
            project={projectForAi}
            scriptHasMinimalRunContent={scriptHasMinimalRunContent}
            persistDraftBeforeRun={editorBridge?.persistDraftBeforeRun}
          />
        ) : null}
      </ProjectScriptAiHubContext.Provider>
    </ScriptAiHubEditorBridgeContext.Provider>
  );
}
